import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { Fragment, useEffect, useRef } from 'react';
import {
  BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG,
  BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
  DEFAULT_SPRING_CONFIG_INTRO,
} from '@experiences/blocs/utils/animations';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  IconButton,
  Icons,
  SegmentedControl,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { LayoutAnimation, LayoutChangeEvent } from 'react-native';
import { OrderEntryMobileBuildModeIcon } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-mobile-build-mode-icon';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderMetric } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-order-metrics';
import { OrderSummaryMetric } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-summary-metric';
import { shadow } from '@/styles';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const MOUNT_ANIMATION_DELAY = 400;
const EXPAND_DURATION_MS = 340;
const EXPAND_EASING = Easing.bezier(0.32, 0.72, 0, 1);
const METRICS_PER_ROW = 3;
const DIVIDER_HEIGHT = 26;
const CARD_BORDER_WIDTH = 1;
const TOGGLE_ICON_SIZE = '$24';

const BAR_SHADOW = shadow({
  opacity: 0.12,
  radius: 24,
  x: 0,
  y: 12,
});

interface OrderEntryMobileSummaryBarProps {
  expanded: boolean;
  hasItemsInCart: boolean;
  metrics: OrderMetric[];
  mode: OrderEntryMode;
  onCheckout: () => void;
  /** Reports the collapsed bar's height above the nav, margin included. */
  onClearanceChange: (clearance: number) => void;
  onModeChange: (mode: OrderEntryMode) => void;
  /** Reports the expandable panel's measured height. */
  onPanelHeightChange: (height: number) => void;
  onToggleExpanded: () => void;
  searchPillElement?: React.ReactNode;
}

export const OrderEntryMobileSummaryBar: React.FC<
  OrderEntryMobileSummaryBarProps
> = ({
  expanded,
  hasItemsInCart,
  metrics,
  mode,
  onCheckout,
  onClearanceChange,
  onModeChange,
  onPanelHeightChange,
  onToggleExpanded,
  searchPillElement,
}) => {
  const [styles, theme] = useStyles();
  const bottomNavHeight = useExperienceUIStore(
    (state) => state.bottomNavHeight,
  );
  const isBottomNavHidden = useExperienceUIStore(
    (state) => state.isBottomNavHidden,
  );
  const insets = useSafeAreaInsets();

  const translateY = useSharedValue(100);
  useEffect(() => {
    translateY.value = withDelay(
      MOUNT_ANIMATION_DELAY,
      withSpring(0, { ...DEFAULT_SPRING_CONFIG_INTRO, velocity: 0 }),
    );
  }, [translateY]);

  // moves down with the bottom nav when it hides on scroll, staying above
  // the home indicator so the screen corners don't clip the bar
  const hideOffset = useSharedValue(0);
  useEffect(() => {
    hideOffset.value = withTiming(
      isBottomNavHidden ? Math.max(bottomNavHeight - insets.bottom, 0) : 0,
      isBottomNavHidden
        ? BOTTOM_NAV_BAR_HIDE_TIMING_CONFIG
        : BOTTOM_NAV_BAR_REVEAL_TIMING_CONFIG,
    );
  }, [bottomNavHeight, hideOffset, insets.bottom, isBottomNavHidden]);

  // animates the panel between 0 and its measured height
  const panelHeight = useSharedValue(0);
  const expandProgress = useSharedValue(0);
  useEffect(() => {
    expandProgress.value = withTiming(expanded ? 1 : 0, {
      duration: EXPAND_DURATION_MS,
      easing: EXPAND_EASING,
    });
  }, [expanded, expandProgress]);

  const { spaceMedium, spaceSmall } = theme.sk.spacing;
  const mountStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value + hideOffset.value }],
  }));

  // animates the widening of the panel when expanded
  const handleToggleExpanded = () => {
    LayoutAnimation.configureNext({
      duration: EXPAND_DURATION_MS,
      update: {
        type: LayoutAnimation.Types.easeInEaseOut,
      },
    });
    onToggleExpanded();
  };
  const panelStyle = useAnimatedStyle(() => ({
    height: expandProgress.value * panelHeight.value,
    opacity: expandProgress.value,
  }));

  const lastPanelHeight = useRef(-1);
  const handlePanelLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height === lastPanelHeight.current) {
      return;
    }
    lastPanelHeight.current = height;
    panelHeight.value = height;
    onPanelHeightChange(height);
  };

  const lastControlsRowHeight = useRef(-1);
  const handleControlsRowLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height === lastControlsRowHeight.current) {
      return;
    }
    lastControlsRowHeight.current = height;
    onClearanceChange(
      height + CARD_BORDER_WIDTH * 2 + theme.sk.spacing.spaceSmall,
    );
  };

  const metricRows: OrderMetric[][] = [];
  for (let i = 0; i < metrics.length; i += METRICS_PER_ROW) {
    metricRows.push(metrics.slice(i, i + METRICS_PER_ROW));
  }

  return (
    <Animated.View
      pointerEvents="box-none"
      style={[
        styles.container,
        {
          bottom: bottomNavHeight,
          paddingHorizontal: expanded ? spaceSmall : spaceMedium,
        },
        mountStyle,
      ]}
    >
      {searchPillElement}
      <YStack
        borderRadius="$12"
        marginBottom={theme.sk.spacing.spaceSmall}
        style={BAR_SHADOW}
      >
        <YStack
          accessibilityLabel={i18n.t(
            'sales_portal.order_entry.order_summary_bottom_bar.title',
          )}
          backgroundColor="$bgPrimaryDefault"
          borderColor="$borderPrimary"
          borderRadius="$12"
          borderWidth={CARD_BORDER_WIDTH}
        >
          <XStack
            alignItems="center"
            gap="$8"
            onLayout={handleControlsRowLayout}
            padding="$8"
          >
            <IconButton
              aria-label={i18n.t(
                expanded
                  ? 'sales_portal.order_entry.order_summary_bottom_bar.collapse'
                  : 'sales_portal.order_entry.order_summary_bottom_bar.expand',
              )}
              icon={
                expanded ? (
                  <Icons.X size={TOGGLE_ICON_SIZE} />
                ) : (
                  <Icons.ChevronDown size={TOGGLE_ICON_SIZE} />
                )
              }
              onPress={handleToggleExpanded}
              shape="circle"
              size="$md"
              variant="tertiary"
            />
            <XStack
              alignItems="center"
              alignSelf="center"
              flex={1}
              justifyContent="center"
            >
              <SegmentedControl
                aria-label={i18n.t(
                  'sales_portal.order_entry.order_summary_bottom_bar.view_toggle',
                )}
                fullWidth
                items={[
                  {
                    children: (
                      <SegmentedControl.Icon>
                        <OrderEntryMobileBuildModeIcon />
                      </SegmentedControl.Icon>
                    ),
                    label: i18n.t('sales_portal.order_entry.control.build'),
                    value: OrderEntryMode.Build,
                  },
                  {
                    children: (
                      <SegmentedControl.Icon>
                        <Icons.CheckCircle />
                      </SegmentedControl.Icon>
                    ),
                    label: i18n.t('sales_portal.order_entry.control.review'),
                    value: OrderEntryMode.Review,
                  },
                ]}
                onValueChange={(value) =>
                  onModeChange(
                    value === OrderEntryMode.Review
                      ? OrderEntryMode.Review
                      : OrderEntryMode.Build,
                  )
                }
                size="$lg"
                value={mode}
              />
            </XStack>
            <YStack
              alignSelf="center"
              backgroundColor="$borderPrimary"
              height={DIVIDER_HEIGHT}
              width={1}
            />
            <YStack flex={1}>
              <Button
                accessibilityLabel={i18n.t('sales_portal.bottom_bar.checkout')}
                disabled={!hasItemsInCart}
                onPress={onCheckout}
                size={ButtonSize.Medium}
                variant={ButtonVariant.Primary}
              >
                {i18n.t('sales_portal.bottom_bar.checkout')}
              </Button>
            </YStack>
          </XStack>
          <Animated.View
            aria-hidden={!expanded}
            pointerEvents={expanded ? 'auto' : 'none'}
            style={[styles.panel, panelStyle]}
          >
            <YStack
              left={0}
              onLayout={handlePanelLayout}
              padding="$8"
              paddingTop={0}
              position="absolute"
              right={0}
              top={0}
            >
              <YStack
                backgroundColor={theme.sk.color.bg.card.mid}
                borderRadius="$12"
                gap="$16"
                padding="$16"
              >
                {metricRows.map((row, rowIndex) => (
                  <XStack gap="$12" key={rowIndex}>
                    {row.map((metric, columnIndex) => (
                      <Fragment key={metric.metricKey}>
                        {columnIndex > 0 ? (
                          <YStack
                            alignSelf="stretch"
                            backgroundColor="$borderPrimary"
                            width={1}
                          />
                        ) : null}
                        <YStack flex={1} minWidth={0}>
                          <OrderSummaryMetric
                            isLoading={metric.isLoading}
                            title={metric.title}
                            value={metric.value}
                            valueVariant="paragraph-base-600"
                          />
                        </YStack>
                      </Fragment>
                    ))}
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </Animated.View>
        </YStack>
      </YStack>
    </Animated.View>
  );
};

const useStyles = createStyles(() => ({
  container: {
    left: 0,
    position: 'absolute',
    right: 0,
  },
  panel: {
    overflow: 'hidden',
  },
}));
